import { Typeface } from "@/foundations/typography/typography-classes";
import { cn } from "@/lib/utils";
import * as React from "react";
import {
  TextVariantProps,
  textVariants,
  ElementType as VariantElementType,
} from "./text-variants";

type BaseTextProps = Omit<TextVariantProps, "prefix" | "suffix" | "element"> & {
  className?: string;
  children?: React.ReactNode;
  typeface?: Typeface;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  spacing?: number;
  testId?: string;
};

// Generic prop type that infers allowed props based on the element
export type TextProps<T extends React.ElementType> = BaseTextProps & {
  element?: T;
} & Omit<
    React.ComponentPropsWithRef<T>,
    keyof BaseTextProps | "as" | "ref" | "element"
  >;

// Generic component definition using forwardRef
const TextInner = <T extends React.ElementType = "span">(
  {
    className,
    children,
    element,
    typeface = "body-2",
    prefix,
    suffix,
    spacing = 8,
    testId,
    ...rest
  }: TextProps<T>,
  ref: React.Ref<HTMLElement>,
) => {
  const Element = element || "span";

  return (
    <Element
      ref={ref}
      data-testid={testId}
      className={cn(
        `tw-${typeface}`,
        textVariants({
          prefix: !!prefix,
          suffix: !!suffix,
          element: Element as VariantElementType,
          spacing,
          className,
        }),
      )}
      {...rest}
    >
      {prefix && <span className="tw-flex-none tw-inline-flex">{prefix}</span>}
      {children}
      {suffix && <span className="tw-flex-none tw-inline-flex">{suffix}</span>}
    </Element>
  );
};

// Wrap in forwardRef with proper generic signature
export const Text = React.forwardRef(TextInner) as <
  T extends React.ElementType = "span",
>(
  props: TextProps<T> & { ref?: React.Ref<HTMLElement> },
) => React.ReactElement | null;

(Text as React.ComponentType).displayName = "Text";
